import React, { useContext, useEffect, useState } from 'react';
import {
    SkeletonLoader,
    TimelineStepProps,
    useMultipleLoading,
    useSkeletonMetrics,
    useProgressiveSkeleton,
    BoxSkeleton,
} from '@europrocurement/flexy-components';
import {
    DossierCompany,
    DossierFormalites,
    DossierInformations,
    DossierSiblings,
    DossierStepContext,
    useCurrentDossierCtx,
} from '@europrocurement/l2d-modules';
import {
    selectedPrescripteursSelector,
    useTiersService,
    useTiersServiceSelector,
    useSociete,
} from '@europrocurement/l2d-domain';
import { TiersTiersPrescripteurReadCommerciaux } from '@europrocurement/l2d-domain/openApi/ApiTiers';
import { AxiosResponse } from 'axios';

type CommercialInfo = {
    nom: string;
    prenom: string;
    userIdentifier: string;
    userId?: number;
};

const InformationsTab: React.FunctionComponent = function () {
    const { dossier, siblings } = useCurrentDossierCtx();
    const { steps } = useContext(DossierStepContext);

    const [commercialInfo, setCommercialInfo] = useState<CommercialInfo | null>(null);
    const [isLoadingCommercial, setIsLoadingCommercial] = useState(false);

    const { prescribersApi } = useTiersService();
    const xIdSociete = useSociete();
    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);

    const { showSkeletonWhen, isAnyLoading } = useMultipleLoading({
        loadingStates: {
            dossier: dossier === null,
            prescripteur: prescripteur === undefined,
            commercial: isLoadingCommercial,
        },
    });

    const { trackSkeletonStart, trackSkeletonEnd } = useSkeletonMetrics({
        componentName: 'InformationsTab',
    });

    const { completeStep, isStepLoading } = useProgressiveSkeleton({
        steps: ['dossier', 'prescripteur', 'commercial'],
    });

    // Track loading metrics
    useEffect(() => {
        if (isAnyLoading) {
            trackSkeletonStart();
        } else {
            trackSkeletonEnd();
        }
    }, [isAnyLoading, trackSkeletonStart, trackSkeletonEnd]);

    // Progressive loading
    useEffect(() => {
        if (dossier !== null) completeStep('dossier');
    }, [dossier, completeStep]);

    useEffect(() => {
        if (prescripteur !== undefined) completeStep('prescripteur');
    }, [prescripteur, completeStep]);

    useEffect(() => {
        if (!isLoadingCommercial && commercialInfo !== null) {
            completeStep('commercial');
        }
    }, [isLoadingCommercial, commercialInfo, completeStep]);

    let currentFound = false;
    const displayedSteps: Array<TimelineStepProps> = steps.map((step) => {
        const isCurrent = dossier ? Number(step.id) === Number(dossier.stepId) : false;
        currentFound = currentFound || isCurrent;

        return {
            label: step.label as string,
            icon: !currentFound || isCurrent ? 'check' : undefined,
            current: isCurrent,
        };
    });

    const siblingsFiltered =
        siblings && dossier
            ? siblings.filter((item) => Number(item.id) !== Number(dossier.id))
            : [];

    useEffect(() => {
        const loadCommercialInfo = async () => {
            if (!dossier?.prescripteurId || !xIdSociete) {
                console.debug('📋 Informations manquantes pour charger le commercial:', {
                    prescripteurId: dossier?.prescripteurId,
                    xIdSociete,
                });
                return;
            }

            console.debug('🔄 Chargement des informations du commercial...');
            setIsLoadingCommercial(true);

            try {
                const response: AxiosResponse =
                    await prescribersApi.getCommerciauxPrescripteurTiersItem({
                        id: dossier.prescripteurId.toString(),
                        xIdSociete,
                    });

                const commercialUsers: TiersTiersPrescripteurReadCommerciaux['commerciaux'] =
                    response?.data?.commerciaux;

                if (commercialUsers && commercialUsers.length > 0) {
                    const matchingCommercial = commercialUsers.find(
                        (commercial) => commercial.actif && commercial.domaine?.id === 3,
                    );

                    if (matchingCommercial) {
                        const formattedCommercialInfo: CommercialInfo = {
                            nom: matchingCommercial.utilisateur?.nom || '',
                            prenom: matchingCommercial.utilisateur?.prenom || '',
                            userIdentifier: matchingCommercial.utilisateur?.userIdentifier || '',
                            userId: matchingCommercial.utilisateur?.id,
                        };

                        console.debug(
                            '✅ Informations du commercial récupérées:',
                            formattedCommercialInfo,
                        );
                        setCommercialInfo(formattedCommercialInfo);
                    } else {
                        console.debug(
                            '⚠️ Commercial non trouvé dans la liste pour ce prescripteur',
                        );
                        setCommercialInfo({ nom: '', prenom: '', userIdentifier: '' });
                    }
                } else {
                    console.debug('⚠️ Aucun commercial trouvé pour ce prescripteur');
                    setCommercialInfo({ nom: '', prenom: '', userIdentifier: '' });
                }
            } catch (error) {
                console.error(
                    '❌ Erreur lors du chargement des informations du commercial:',
                    error,
                );
                setCommercialInfo({ nom: '', prenom: '', userIdentifier: '' });
            } finally {
                setIsLoadingCommercial(false);
            }
        };

        loadCommercialInfo();
    }, [dossier?.prescripteurId, prescribersApi, xIdSociete]);

    if (!dossier) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    }
    return (
        <>
            <SkeletonLoader
                isLoading={showSkeletonWhen(['dossier', 'prescripteur', 'commercial'])}
                containerSx={{
                    marginTop: '10px',
                    marginBottom: '10px',
                }}
                skeleton={
                    <BoxSkeleton
                        sx={{
                            height: '250px',
                        }}
                    />
                }
            >
                <DossierInformations
                    steps={displayedSteps}
                    dossierId={dossier.id as number}
                    contactNom={dossier.contactNom as string}
                    contactPrenom={dossier.contactPrenom as string}
                    contactUserIdentifier={dossier.contactUserIdentifier as string}
                    contactId={dossier.contactId as number}
                    commercialNom={commercialInfo?.nom || ''}
                    commercialPrenom={commercialInfo?.prenom || ''}
                    commercialUserIdentifier={commercialInfo?.userIdentifier || ''}
                    commercialId={commercialInfo?.userId}
                    formalisteId={dossier.formalisteId as number}
                    formalisteNom={dossier.formalisteNom as string}
                    formalistePrenom={dossier.formalistePrenom as string}
                    formalisteUserIdentifier={dossier.formalisteUserIdentifier as string}
                    critical={dossier.critical as boolean}
                    dateUpdate={dossier.dateUpdate as string}
                    dossier={dossier}
                    source="FRONT"
                />
            </SkeletonLoader>

            <SkeletonLoader
                isLoading={isStepLoading('dossier')}
                containerSx={{
                    marginTop: '10px',
                    marginBottom: '10px',
                }}
                skeleton={
                    <BoxSkeleton
                        sx={{
                            height: '250px',
                        }}
                    />
                }
            >
                <DossierFormalites
                    formalities={dossier.formalites.map((formality) => ({
                        label: formality.libelle,
                    }))}
                />
            </SkeletonLoader>

            <SkeletonLoader
                isLoading={isStepLoading('dossier')}
                containerSx={{
                    marginTop: '10px',
                    marginBottom: '10px',
                }}
                skeleton={
                    <BoxSkeleton
                        sx={{
                            height: '250px',
                        }}
                    />
                }
            >
                <>
                    <DossierCompany
                        companyName={dossier.societeDenomination as string}
                        companyIdentifier={dossier.clientOpereSiren as string}
                        companyAddress={dossier.clientOpereAdresseComplete as string}
                    />
                    {siblingsFiltered.length > 0 && (
                        <DossierSiblings
                            orderNumber={dossier.collectionNumero as string}
                            siblings={siblingsFiltered}
                        />
                    )}
                </>
            </SkeletonLoader>
        </>
    );
};

export default InformationsTab;
